import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext, useReducer } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '@fortawesome/fontawesome-free/css/all.min.css';

// ==============================================
// API SERVICE
// ==============================================
const BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:4000';

async function http(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => '');
  if (!res.ok) {
    const msg = typeof body === 'string' ? body : (body?.error?.message || body?.message || JSON.stringify(body));
    const err = new Error(`HTTP ${res.status}: ${msg}`);
    err.status = res.status; err.body = body;
    throw err;
  }
  return body;
}

const api = {
  login: (email, password) => http('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: (token) => http('/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
  refresh: (refreshToken) => http('/auth/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }),
  listPages: (token) => http('/admin/pages', { headers: { Authorization: `Bearer ${token}` } }),
  getPage: (token, id) => http(`/admin/pages/${encodeURIComponent(id)}`, { headers: { Authorization: `Bearer ${token}` } }),
  createPage: (token, payload) => http('/admin/pages', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) }),
  updatePage: (token, id, payload) => http(`/admin/pages/${encodeURIComponent(id)}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) }),
  patchPage: (token, id, payload) => http(`/admin/pages/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload)
  }),
  publishPage: (token, id) => http(`/admin/pages/${encodeURIComponent(id)}/publish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ status: 'PUBLISHED' }) }),
  unpublishPage: (token, id) => http(`/admin/pages/${encodeURIComponent(id)}/unpublish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  saveTemplate: (token, slug, html) => http('/admin/pages/template', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ slug, html }) }),
};

// ==============================================
// PAGE TEMPLATES
// ==============================================


// ==============================================
// UTILITY FUNCTIONS
// ==============================================
function extractLegacySection(layoutJson) {
  if (!Array.isArray(layoutJson)) return { index: -1, section: null };
  const idx = layoutJson.findIndex(s => s && s.type === 'LegacyCmsPage' && s.props);
  return { index: idx, section: idx >= 0 ? layoutJson[idx] : null };
}

function upsertLegacySection(layoutJson, legacyProps) {
  const safe = Array.isArray(layoutJson) ? [...layoutJson] : [];
  const { index } = extractLegacySection(safe);
  const section = { type: 'LegacyCmsPage', props: legacyProps };
  if (index >= 0) safe[index] = section; else safe.unshift(section);
  return safe;
}

function groupKeys(keysObj) {
  const keys = Object.keys(keysObj || {}).sort();
  const groups = {};
  for (const k of keys) {
    const parts = k.split('.');
    const group = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : (parts[0] || 'misc');
    if (!groups[group]) groups[group] = [];
    groups[group].push(k);
  }
  return groups;
}

function isProbablyRichText(value) {
  if (typeof value !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(value.trim());
}

function cloneDeep(v) {
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return v;
  }
}

// ==============================================
// STORE
// ==============================================
const StoreCtx = createContext(null);
const initialState = {
  auth: {
    accessToken: localStorage.getItem('rc_token') || '',
    refreshToken: localStorage.getItem('rc_refresh') || '',
    user: null,
  },
  pages: { list: [], byId: {} },
  ui: { loading: false, toasts: [] },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING': return { ...state, ui: { ...state.ui, loading: action.payload } };
    case 'ADD_TOAST': return { ...state, ui: { ...state.ui, toasts: [...state.ui.toasts, action.payload] } };
    case 'REMOVE_TOAST': return { ...state, ui: { ...state.ui, toasts: state.ui.toasts.filter(t => t.id !== action.payload) } };
    case 'SET_TOKENS': {
      const { accessToken, refreshToken } = action.payload;
      if (accessToken !== undefined) localStorage.setItem('rc_token', accessToken || '');
      if (refreshToken !== undefined) localStorage.setItem('rc_refresh', refreshToken || '');
      return { ...state, auth: { ...state.auth, accessToken: accessToken ?? state.auth.accessToken, refreshToken: refreshToken ?? state.auth.refreshToken } };
    }
    case 'SET_USER': return { ...state, auth: { ...state.auth, user: action.payload } };
    case 'LOGOUT':
      localStorage.removeItem('rc_token'); localStorage.removeItem('rc_refresh');
      return { ...state, auth: { accessToken: '', refreshToken: '', user: null }, pages: { list: [], byId: {} } };
    case 'SET_PAGES': return { ...state, pages: { ...state.pages, list: Array.isArray(action.payload) ? action.payload : [] } };
    case 'CACHE_PAGE': return { ...state, pages: { ...state.pages, byId: { ...state.pages.byId, [action.payload.id]: action.payload.page } } };
    case 'UPDATE_CACHED_PAGE': {
      const existing = state.pages.byId[action.payload.id] || {};
      return { ...state, pages: { ...state.pages, byId: { ...state.pages.byId, [action.payload.id]: { ...existing, ...action.payload.updates } } } };
    }
    default: return state;
  }
}

function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const actions = useMemo(() => ({
    setLoading: v => dispatch({ type: 'SET_LOADING', payload: !!v }),
    toast: (msg, kind = 'info') => {
      const id = Date.now() + Math.random();
      dispatch({ type: 'ADD_TOAST', payload: { id, msg, kind } });
      setTimeout(() => dispatch({ type: 'REMOVE_TOAST', payload: id }), 4000);
    },
    setTokens: tokens => dispatch({ type: 'SET_TOKENS', payload: tokens }),
    setUser: user => dispatch({ type: 'SET_USER', payload: user }),
    logout: () => dispatch({ type: 'LOGOUT' }),
    setPages: list => dispatch({ type: 'SET_PAGES', payload: list }),
    cachePage: (id, page) => dispatch({ type: 'CACHE_PAGE', payload: { id, page } }),
    updateCachedPage: (id, updates) => dispatch({ type: 'UPDATE_CACHED_PAGE', payload: { id, updates } }),
  }), []);
  return <StoreCtx.Provider value={{ state, actions }}>{children}</StoreCtx.Provider>;
}
const useStore = () => { const c = useContext(StoreCtx); if (!c) throw new Error('useStore outside Provider'); return c; };

// ==============================================
// ROUTER
// ==============================================
const RouterCtx = createContext(null);
function RouterProvider({ children }) {
  const [hash, setHash] = useState(window.location.hash || '#/');
  useEffect(() => {
    const handle = () => setHash(window.location.hash || '#/');
    window.addEventListener('hashchange', handle);
    return () => window.removeEventListener('hashchange', handle);
  }, []);
  const navigate = useCallback(to => { window.location.hash = to; }, []);
  const route = hash.replace('#', '') || '/';
  return <RouterCtx.Provider value={{ route, navigate }}>{children}</RouterCtx.Provider>;
}
const useRouter = () => useContext(RouterCtx);

// ==============================================
// TOAST COMPONENT
// ==============================================
function ToastContainer() {
  const { state } = useStore();
  const icons = { success: 'fa-check-circle', error: 'fa-circle-xmark', info: 'fa-circle-info', warn: 'fa-triangle-exclamation' };
  const colors = { success: '#22c55e', error: '#ef4444', info: '#FF9500', warn: '#f59e0b' };

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360 }}>
      {state.ui.toasts.map(t => (
        <div key={t.id} className="bg-white shadow-lg rounded-3 p-3 d-flex align-items-center gap-3" style={{ borderLeft: `3px solid ${colors[t.kind] || colors.info}` }}>
          <i className={`fa-solid ${icons[t.kind] || icons.info}`} style={{ color: colors[t.kind] || colors.info, fontSize: 15 }}></i>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ==============================================
// LOGIN PAGE
// ==============================================
function LoginPage() {
  const { actions } = useStore();
  const { navigate } = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleLogin() {
    setBusy(true);
    try {
      const res = await api.login(email.trim(), password);
      actions.setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      const me = await api.me(res.accessToken);
      actions.setUser(me);
      actions.toast('Welcome back!', 'success');
      navigate('#/pages');
    } catch (e) {
      actions.toast(e.message || 'Login failed', 'error');
    } finally { setBusy(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0d0d16', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '20%', left: '30%', width: 400, height: 400, background: 'radial-gradient(circle,rgba(255,149,0,0.08),transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '20%', right: '25%', width: 300, height: 300, background: 'radial-gradient(circle,rgba(255,179,0,0.06),transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      <div className="bg-white rounded-4 p-5" style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        <div className="d-flex align-items-center gap-3 mb-4">
          <div style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#FF9500,#FFB300)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🚆</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0d0d16' }}>RailCab<span style={{ color: '#FF9500' }}>.in</span></div>
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 1 }}>Admin CMS — Visual Editor</div>
          </div>
        </div>

        <div className="mb-4">
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0d0d16' }}>Sign in</div>
          <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>Manage your site content visually</div>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Email</label>
        <input className="form-control" style={{ marginBottom: 16 }} value={email} onChange={e => setEmail(e.target.value)} placeholder="User name " onKeyDown={e => e.key === 'Enter' && handleLogin()} />

        <label style={{ fontSize: 12, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Password</label>
        <input className="form-control" style={{ marginBottom: 24 }} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" onKeyDown={e => e.key === 'Enter' && handleLogin()} />

        <button className="btn btn-primary w-100 py-3" onClick={handleLogin} disabled={busy} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}>
          {busy ? <><span className="spinner-border spinner-border-sm me-2"></span> Signing in…</> : <><i className="fa-solid fa-right-to-bracket me-2"></i> Sign in</>}
        </button>

        <div className="mt-4 p-3 bg-light rounded-3 small text-secondary">
          <i className="fa-solid fa-circle-info me-2" style={{ color: '#FF9500' }} />
          Connect your API at <code>http://localhost:4000</code> before signing in.
        </div>
      </div>
    </div>
  );
}

// ==============================================
// SIDEBAR
// ==============================================
function Sidebar({ collapsed, onToggle, currentView, onNavigate }) {
  const { state, actions } = useStore();
  const { navigate } = useRouter();

  function logout() {
    actions.logout();
    navigate('#/login');
  }

  const navItems = [
    { id: 'pages', icon: 'fa-file-lines', label: 'All Pages' },
    { id: 'navigation', icon: 'fa-compass', label: 'Navigation' },
    { id: 'editor', icon: 'fa-pen-ruler', label: 'Visual Editor' },
  ];

  return (
    <div style={{ width: collapsed ? 60 : 240, minWidth: collapsed ? 60 : 240, background: '#13131f', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0, zIndex: 50, transition: 'width 0.25s' }}>
      <div style={{ padding: '18px 14px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        {!collapsed && (
          <div className="d-flex align-items-center gap-2" style={{ overflow: 'hidden' }}>
            <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#FF9500,#FFB300)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🚆</div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'white', whiteSpace: 'nowrap' }}>RailCab<span style={{ color: '#FF9500' }}>.in</span></div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1, whiteSpace: 'nowrap' }}>Admin CMS</div>
            </div>
          </div>
        )}
        {collapsed && <div style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#FF9500,#FFB300)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, margin: '0 auto' }}>🚆</div>}
        {!collapsed && <button className="btn btn-sm" onClick={onToggle} style={{ padding: 0, border: 'none', background: 'transparent', color: '#9ca3af' }}><i className="fa-solid fa-bars"></i></button>}
      </div>

      <div style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
        {!collapsed && <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: '#6b7280', textTransform: 'uppercase', padding: '10px 4px 6px' }}>Content</div>}
        {navItems.map(item => (
          <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : ''}
            style={{ display: 'flex', alignItems: 'center', gap: 11, padding: collapsed ? '10px 0' : '9px 12px', borderRadius: 11, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, border: 'none', width: '100%', textAlign: 'left', background: currentView === item.id ? 'rgba(255,149,0,0.12)' : 'transparent', color: currentView === item.id ? '#FF9500' : '#9ca3af', justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <i className={`fa-solid ${item.icon}`} style={{ width: 17, textAlign: 'center', fontSize: 14 }}></i>
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </div>

      <div style={{ padding: '10px 8px 14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        {!collapsed && state.auth.user && (
          <div style={{ padding: '8px 12px', marginBottom: 6, borderRadius: 10, background: 'rgba(255,255,255,0.04)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{state.auth.user.email}</div>
            <div style={{ fontSize: 10, color: '#FF9500', marginTop: 2, fontWeight: 600 }}>{state.auth.user.role || 'ADMIN'}</div>
          </div>
        )}
        {collapsed && <button className="btn btn-sm w-100" onClick={logout} style={{ padding: '10px 0', border: 'none', background: 'transparent', color: '#ef4444' }}><i className="fa-solid fa-right-from-bracket"></i></button>}
        {!collapsed && (
          <>
            <button className="btn w-100 mb-1" onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 11, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, border: 'none', background: 'transparent', color: '#ef4444' }}>
              <i className="fa-solid fa-right-from-bracket" style={{ width: 17, textAlign: 'center' }} />
              <span>Sign out</span>
            </button>
            <button className="btn w-100" onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', borderRadius: 11, cursor: 'pointer', fontSize: 13.5, fontWeight: 500, border: 'none', background: 'transparent', color: '#6b7280' }}>
              <i className="fa-solid fa-chevron-left" style={{ width: 17, textAlign: 'center' }} />
              <span>Collapse</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ==============================================
// TOP BAR
// ==============================================
function TopBar({ currentPage, pages, onPageChange, editMode, onEditToggle, onAddPage, isSidebarCollapsed, onSidebarToggle }) {
  return (
    <div style={{ height: 56, minHeight: 56, background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', flexShrink: 0 }}>
      {isSidebarCollapsed && (
        <button className="btn btn-sm" onClick={onSidebarToggle} style={{ padding: 0, border: 'none', background: 'transparent', color: '#9ca3af' }}><i className="fa-solid fa-bars"></i></button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>Page</span>
        <select className="form-select form-select-sm" style={{ maxWidth: 220, padding: '6px 10px', fontSize: 13, background: 'rgba(12, 12, 12, 0.8)', border: '1px solid rgba(255,255,255,0.07)', color: '#e8e8f4' }} value={currentPage?.id || ''} onChange={e => { const p = pages.find(x => x.id === e.target.value); if (p) onPageChange(p); }}>
          <option value="">— Select a page —</option>
          {pages.map(p => <option key={p.id} value={p.id}>{p.title} ({p.slug})</option>)}
        </select>
        {currentPage && (
          <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            /{currentPage.slug}.html
          </span>
        )}
      </div>

      <div className="d-flex align-items-center gap-2">
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Edit</span>
        <div className={`form-check form-switch`}>
          <input className="form-check-input" type="checkbox" role="switch" id="editMode" checked={editMode} onChange={onEditToggle} style={{ cursor: 'pointer', backgroundColor: editMode ? '#FF9500' : undefined }} />
        </div>
      </div>

      <button className="btn btn-sm btn-outline-secondary" onClick={onAddPage} style={{ color: '#e8e8f4', borderColor: 'rgba(255,255,255,0.07)' }}><i className="fa-solid fa-plus me-1"></i> New Page</button>
    </div>
  );
}

// ==============================================
// INJECT STYLES AND SCRIPTS FOR IFRAME
// ==============================================




// ==============================================
// BUILD IFRAME DOCUMENT
// ==============================================
function buildIframeDoc(templateHtml, keys, collections) {
  let doc = templateHtml || '';
  if (doc.includes('</head>')) {
    doc = doc.replace('</head>', '<link rel="stylesheet" href="/assets/cms-inject.css" />\n</head>');
  }
  if (doc.includes('</body>')) {
    doc = doc.replace('</body>', '<script src="/assets/cms-inject.js"></script>\n</body>');
  }
  return doc;
}

// ==============================================
// PREVIEW FRAME
// ==============================================
// ==============================================
// PREVIEW FRAME - UPDATED
// ==============================================
function PreviewFrame({ page, editMode, onKeyChange, token }) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const lastPageId = useRef(null);
  const { actions } = useStore();

  useEffect(() => {
    if (!page) return;
    const frame = iframeRef.current;
    if (!frame) return;

    setLoading(true);
    lastPageId.current = page.id;



    const layout = page.layoutJson || [];
    const legacy = layout.find(s => s?.type === 'LegacyCmsPage');
    const keys = legacy?.props?.keys || {};
    const collections = legacy?.props?.collections || {};

    let tp = legacy?.props?.template || page.slug || 'home';
    fetch('/templates/' + tp + '.html')
      .then(async res => {
        let text = await res.text();
        if (!res.ok || text.includes('id="root"') || text.includes('<title>RailCab Admin</title>')) {
          const fallbackRes = await fetch('/templates/home.html');
          return fallbackRes.text();
        }
        return text;
      })
      .then(template => {
        const doc = buildIframeDoc(template, keys, collections);
        const handleLoad = () => {
          setLoading(false);
          frame.contentWindow?.postMessage({
            type: 'RC_SET_EDIT_MODE',
            value: editMode
          }, '*');

          frame.contentWindow?.postMessage({
            type: 'RC_APPLY_DATA',
            keys: keys,
            collections: collections
          }, '*');
        };

        frame.onload = handleLoad;
        frame.srcdoc = doc;
      })
      .catch(err => {
        console.error('Failed to load template:', err);
        setLoading(false);
      });
  }, [page?.id, page?.slug]);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame?.contentWindow || !lastPageId.current) return;
    frame.contentWindow.postMessage({
      type: 'RC_SET_EDIT_MODE',
      value: editMode
    }, '*');
  }, [editMode]);

  useEffect(() => {
    const handle = async e => {
      if (e.data?.type === 'RC_CMS_CHANGE') {
        const { key, value, publish } = e.data;

        onKeyChange(key, value, publish);

        const frame = iframeRef.current;
        frame?.contentWindow?.postMessage({
          type: 'RC_APPLY_KEY',
          key,
          value
        }, '*');

        if (page && token) {
          try {
            actions.setLoading(true);

            await api.patchPage(token, page.id, {
              keys: {
                [key]: value
              }
            });

            if (publish) {
              actions.toast(`🚀 Published "${key.split('.').pop()}" to cloud`, 'success');
            } else {
              actions.toast(`💾 Saved "${key.split('.').pop()}" to cloud`, 'info');
            }

            const updatedPage = await api.getPage(token, page.id);
            actions.cachePage(page.id, updatedPage);

          } catch (err) {
            actions.toast(`Save failed: ${err.message}`, 'error');
            console.error('PATCH error:', err);
          } finally {
            actions.setLoading(false);
          }
        }
      }
    };

    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [onKeyChange, page, token, actions]);

  if (!page) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light">
        <div style={{ width: 64, height: 64, background: 'linear-gradient(135deg,rgba(255,149,0,0.15),rgba(255,179,0,0.08))', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🚆</div>
        <div className="text-center mt-3">
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Select a page to preview</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>Choose from the page dropdown above</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
      {loading && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(241,245,249,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, backdropFilter: 'blur(4px)' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Loading preview…</div>
          </div>
        </div>
      )}
      <iframe
        id="preview-iframe"
        ref={iframeRef}
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: 'white' }}
        title="Preview"
      />
    </div>
  );
}

// ==============================================
// KEY EDITOR PANEL
// ==============================================
function KeyEditorPanel({ keysObj, onChange }) {
  const [search, setSearch] = useState('');
  const groups = useMemo(() => groupKeys(keysObj || {}), [keysObj]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    const out = {};
    for (const [g, list] of Object.entries(groups)) {
      const f = list.filter(k => k.toLowerCase().includes(q));
      if (f.length) out[g] = f;
    }
    return out;
  }, [groups, search]);

  const groupNames = Object.keys(filtered).sort();
  const totalKeys = Object.keys(keysObj || {}).length;

  function updateKey(key, val) { onChange({ ...(keysObj || {}), [key]: val }); }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12, padding: 20, background: '#fff', color: '#000' }}>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 2 }}>Content Keys</div>
          <div style={{ fontSize: 12, color: '#4b5563' }}>{totalKeys} keys across {Object.keys(groups).length} sections</div>
        </div>
        <input className="form-control" style={{ width: 200, padding: '7px 12px', fontSize: 12, background: '#fff', border: '1px solid #d1d5db', color: '#000' }} placeholder="Search keys…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {groupNames.length === 0 && (
          <div className="text-center p-5" style={{ color: '#4b5563' }}>
            {search ? 'No keys match your search.' : 'No keys yet. They appear here once the page has CMS data-cms-key attributes.'}
          </div>
        )}
        {groupNames.map(g => (
          <div key={g} style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', border: '1px solid #e5e7eb' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div style={{ width: 3, height: 20, background: '#000', borderRadius: '0 3px 3px 0' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.08em', marginLeft: 8 }}>{g}</span>
              <span style={{ fontSize: 10, color: '#4b5563', marginLeft: 'auto' }}>{filtered[g].length} keys</span>
            </div>
            <div className="row g-2">
              {filtered[g].map(k => {
                const val = keysObj?.[k] ?? '';
                const rich = isProbablyRichText(val);
                const isLong = typeof val === 'string' && val.length > 100;
                const label = k.split('.').pop().replace(/[-_.]/g, ' ');
                return (
                  <div key={k} className="col-12 col-md-6">
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#000', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
                    {(rich || isLong) ? (
                      <textarea className="form-control" style={{ minHeight: 80, fontSize: 13, background: '#fff', border: '1px solid #d1d5db', color: '#000' }} value={val} onChange={e => updateKey(k, e.target.value)} spellCheck={false} />
                    ) : (
                      <input className="form-control" style={{ fontSize: 13, background: '#fff', border: '1px solid #d1d5db', color: '#000' }} value={val} onChange={e => updateKey(k, e.target.value)} />
                    )}
                    <div style={{ fontSize: 9, color: '#6b7280', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{k}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==============================================
// VISUAL EDITOR PAGE
// ==============================================
function VisualEditorPage({ selectedPageId, onPageChange, pages }) {
  const { state, actions } = useStore();
  const token = state.auth.accessToken;

  const [page, setPage] = useState(null);
  const [legacy, setLegacy] = useState({ page: '', keys: {}, collections: {} });
  const [editMode, setEditMode] = useState(true);
  const [activeTab, setActiveTab] = useState('preview');
  const [loading, setLoading] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);

  useEffect(() => {
    if (!selectedPageId) { setPage(null); return; }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const cached = state.pages.byId[selectedPageId];
        const p = cached || await api.getPage(token, selectedPageId);
        if (!cancelled) {
          actions.cachePage(selectedPageId, p);
          setPage(p);
          const layout = p.layoutJson || [];
          const { section } = extractLegacySection(layout);
          const lp = section?.props || { page: p.slug, template: p.slug || 'home', keys: {}, collections: {} };
          setLegacy({ page: lp.page || p.slug, template: lp.template || p.slug || 'home', keys: cloneDeep(lp.keys || {}), collections: cloneDeep(lp.collections || {}) });
        }
      } catch (e) {
        if (!cancelled) actions.toast(e.message || 'Failed to load page', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPageId]);

  const handleKeyChange = useCallback((key, value, publish) => {
    setLegacy(l => ({ ...l, keys: { ...l.keys, [key]: value } }));
  }, []);

  async function save() {
    if (!page) return;

    actions.setLoading(true); setLoading(true);
    try {
      const layout = page.layoutJson || [];
      const updatedLayout = upsertLegacySection(Array.isArray(layout) ? layout : [], legacy);

      const payload = { layoutJson: updatedLayout };

      const saved = await api.updatePage(token, page.id, payload);
      actions.cachePage(page.id, saved);
      actions.setPages(state.pages.list.map(p => p.id === page.id ? { ...p, ...saved } : p));
      actions.toast('✅ Page saved successfully', 'success');
    } catch (e) { actions.toast(e.message || 'Save failed', 'error'); }
    finally { actions.setLoading(false); setLoading(false); }
  }

  async function publish() {
    if (!page) return;
    await save();
    actions.setLoading(true); setLoading(true);
    try {
      await api.publishPage(token, page.id);
      const updatedPage = await api.getPage(token, page.id);
      actions.cachePage(page.id, updatedPage);
      setPage(updatedPage);
      actions.setPages(state.pages.list.map(p => p.id === page.id ? updatedPage : p));
      actions.toast('🚀 Page published! Live on site.', 'success');
    } catch (e) { actions.toast(e.message || 'Publish failed', 'error'); }
    finally { actions.setLoading(false); setLoading(false); }
  }

  async function unpublish() {
    if (!page) return;
    if (!window.confirm(`Unpublish "${page.title}" and revert to draft?`)) return;
    actions.setLoading(true); setLoading(true);
    try {
      await api.unpublishPage(token, page.id);
      const updatedPage = await api.getPage(token, page.id);
      actions.cachePage(page.id, updatedPage);
      setPage(updatedPage);
      actions.setPages(state.pages.list.map(p => p.id === page.id ? updatedPage : p));
      actions.toast('🛑 Page unpublished.', 'success');
    } catch (e) { actions.toast(e.message || 'Unpublish failed', 'error'); }
    finally { actions.setLoading(false); setLoading(false); }
  }

  const previewPage = useMemo(() => {
    if (!page) return null;
    const layout = page.layoutJson || [];
    const updated = upsertLegacySection(Array.isArray(layout) ? layout : [], legacy);
    return { ...page, layoutJson: updated };
  }, [page, legacy]);

  const PANEL_TABS = [
    { id: 'preview', label: 'Preview', icon: 'fa-eye' },
    { id: 'keys', label: 'Keys', icon: 'fa-pen-to-square' },
  ];

  if (!selectedPageId) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100">
        <div style={{ fontSize: 48 }}>🎨</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>Visual Editor</div>
        <div style={{ color: '#6b7280', fontSize: 14 }}>Select a page from "All Pages" or the dropdown above</div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, overflowX: 'auto' }}>
          {PANEL_TABS.map(t => (
            <button key={t.id} className={`btn btn-sm ${activeTab === t.id ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setActiveTab(t.id)} style={{ border: 'none', background: activeTab === t.id ? 'rgba(255,149,0,0.15)' : 'transparent', color: activeTab === t.id ? '#FF9500' : '#9ca3af' }}>
              <i className={`fa-solid ${t.icon} me-1`} /> {t.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {activeTab === 'preview' && (
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Edit Mode</span>
              <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" role="switch" id="editModePreview" checked={editMode} onChange={() => setEditMode(v => !v)} style={{ cursor: 'pointer' }} />
              </div>
            </div>
          )}
          <button className="btn btn-sm" onClick={() => setShowRightPanel(v => !v)} style={{ background: showRightPanel ? 'rgba(255,149,0,0.15)' : 'rgba(255,255,255,0.06)', color: showRightPanel ? '#FF9500' : '#9ca3af', border: 'none' }}>
            <i className="fa-solid fa-panel-right"></i>
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {activeTab === 'preview' && (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
              <PreviewFrame
                page={previewPage}
                editMode={editMode}
                onKeyChange={handleKeyChange}
                token={token}
              />
            </div>
          )}
          {activeTab === 'keys' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <KeyEditorPanel keysObj={legacy.keys} onChange={keys => setLegacy(l => ({ ...l, keys }))} />
            </div>
          )}
        </div>
      </div>

      {showRightPanel && (
        <div style={{ width: 280, minWidth: 280, background: '#13131f', borderLeft: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'white', marginBottom: 2 }}>Page Info</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{page?.slug || '–'}</div>
          </div>
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Status</div>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${page?.status === 'PUBLISHED' ? 'bg-success' : 'bg-warning'}`}>
                  {page?.status || 'Draft'}
                </span>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Content</div>
              <div className="d-flex gap-2 flex-wrap">
                <div className="bg-secondary bg-opacity-10 rounded-3 p-2 text-center flex-fill">
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#FF9500' }}>{Object.keys(legacy.keys || {}).length}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Keys</div>
                </div>
              </div>
            </div>

            {page?.updatedAt && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Last Updated</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(page.updatedAt).toLocaleString()}</div>
              </div>
            )}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-outline-secondary btn-sm w-100" onClick={save} disabled={loading} style={{ color: '#e8e8f4' }}>
                {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fa-solid fa-floppy-disk me-2"></i>} Save Draft
              </button>
              {page?.status === 'PUBLISHED' ? (
                <button className="btn btn-outline-warning btn-sm w-100" onClick={unpublish} disabled={loading}>
                  <i className="fa-solid fa-eye-slash me-2"></i> Unpublish
                </button>
              ) : (
                <button className="btn btn-primary btn-sm w-100" onClick={publish} disabled={loading} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}>
                  <i className="fa-solid fa-rocket me-2"></i> Publish Live
                </button>
              )}
            </div>

            <div className="p-3 small text-secondary" style={{ background: 'rgba(255,149,0,0.05)', border: '1px solid rgba(255,149,0,0.12)', borderRadius: 10 }}>
              <strong style={{ color: '#FF9500' }}>💡 Tip:</strong> In Preview tab with Edit Mode on, hover any text on the page and click to edit it inline. Changes save instantly to cloud.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==============================================
// ADD PAGE MODAL — ENHANCED 2-MODE WIZARD
// ==============================================
function countCmsKeys(html) {
  return (html.match(/data-cms-key=/g) || []).length;
}

const EXAMPLE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Page Title</title>
  <link rel="stylesheet" href="/assets/css/styles.css" />
</head>
<body>

  <!-- ===== HERO SECTION ===== -->
  <!-- Replace "your-page" with your actual page slug everywhere below -->
  <section class="hero-section">
    <div class="container">
      <h1 data-cms-key="your-page.hero.content.heading1.text">Page Heading</h1>
      <p data-cms-key="your-page.hero.content.text1.text">A short subtitle or introduction line goes here.</p>
      <a href="#" data-cms-key="your-page.hero.content.button1.text" class="btn btn-primary">Get Started</a>
    </div>
  </section>

  <!-- ===== MAIN CONTENT SECTION ===== -->
  <section class="content-section">
    <div class="container">
      <h2 data-cms-key="your-page.main.content.heading1.text">Main Section Title</h2>
      <p data-cms-key="your-page.main.content.text1.text">First paragraph of your main content. This text is fully editable from the CMS.</p>
      <p data-cms-key="your-page.main.content.text2.text">Second paragraph with additional details about this section.</p>
    </div>
  </section>

  <!-- ===== CALL TO ACTION SECTION ===== -->
  <section class="cta-section">
    <div class="container">
      <h2 data-cms-key="your-page.cta.content.heading1.text">Ready to Get Started?</h2>
      <p data-cms-key="your-page.cta.content.text1.text">Contact us today and we will help you every step of the way.</p>
      <a href="/contact" data-cms-key="your-page.cta.content.button1.text" class="btn btn-secondary">Contact Us</a>
    </div>
  </section>

</body>
</html>`;
/* KEY FORMAT REFERENCE
   Every editable element MUST have:  data-cms-key="page-slug.section-id.content.field-name.type"
   ┌─────────────┬────────────┬─────────┬──────────────┬──────┐
   │  page-slug  │ section-id │ content │  field-name  │ type │
   │  (url slug) │ (any name) │ (fixed) │  (any name)  │ text │
   └─────────────┴────────────┴─────────┴──────────────┴──────┘
   Example keys:
     about-us.hero.content.heading1.text   → main heading in hero
     about-us.hero.content.text1.text      → paragraph in hero
     about-us.main.content.heading1.text   → main section title
     about-us.cta.content.button1.text     → CTA button label
*/

function AddPageModal({ onClose, onCreated }) {
  const { state, actions } = useStore();
  const [step, setStep] = useState('mode'); // 'mode' | 'config'
  const [mode, setMode] = useState(''); // 'template' | 'builder'
  const [selectedLayout, setSelectedLayout] = useState(null);
  // Raw HTML sub-mode: 'existing' (dropdown) or 'custom' (paste HTML)
  const [htmlMode, setHtmlMode] = useState('existing');
  const [customHtml, setCustomHtml] = useState('');
  const [htmlError, setHtmlError] = useState('');
  const [showExample, setShowExample] = useState(false);
  const [form, setForm] = useState({
    title: '', slug: '', navLabel: '', template: 'home',
    showInNav: true, showInFooter: false, navOrder: 99, metaDescription: '',
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const autoSlug = t => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  // Live key count for the custom HTML textarea
  const liveKeyCount = useMemo(() => countCmsKeys(customHtml), [customHtml]);

  const TEMPLATES = [
    { value: 'home', desc: 'Full landing page with hero sections' },
    { value: 'services', desc: 'Services showcase with icon cards' },
    { value: 'company', desc: 'About / Company profile page' },
    { value: 'gallery', desc: 'Image gallery with lightbox' },
    { value: 'news-csr', desc: 'News & CSR card grid page' },
    { value: 'contact', desc: 'Contact form with map' },
    { value: 'faqs', desc: 'FAQ accordion page' },
    { value: 'privacy', desc: 'Privacy policy / legal text' },
    { value: 'terms', desc: 'Terms & conditions text' },
    { value: 'service-details', desc: 'Single service detail page' },
  ];

  const LAYOUTS = [
    {
      id: 'hero-landing', label: 'Hero Landing', icon: 'fa-house', template: 'home',
      desc: 'Full-page hero with feature sections & CTA',
      preview: '┌───────────┐\n│  HERO BG  │\n├─┬─┬─┬─┬─┤\n│ │ │ │ │ │\n│F│F│F│F│F│\n├───────────┤\n│    CTA    │\n└───────────┘',
    },
    {
      id: 'services-grid', label: 'Services Grid', icon: 'fa-th-large', template: 'services',
      desc: 'Services showcase with icon card grid',
      preview: '┌───────────┐\n│  HERO BG  │\n├───┬───┬───┤\n│ ■ │ ■ │ ■ │\n│   │   │   │\n├───┴───┴───┤\n│  FEATURES │\n└───────────┘',
    },
    {
      id: 'legal-text', label: 'Legal / Content', icon: 'fa-file-lines', template: 'privacy',
      desc: 'Hero banner + rich formatted text content',
      preview: '┌───────────┐\n│  HERO BG  │\n├───────────┤\n│           │\n│ ■■■■■■■■  │\n│ ■■■  ■■■  │\n│ ■■■■■■■■  │\n└───────────┘',
    },
    {
      id: 'news-cards', label: 'News & Cards', icon: 'fa-newspaper', template: 'news-csr',
      desc: 'Hero + card grid for news & updates',
      preview: '┌───────────┐\n│  HERO BG  │\n├───┬───┬───┤\n│[C]│[C]│[C]│\n│   │   │   │\n├───┴───┴───┤\n│    CTA    │\n└───────────┘',
    },
  ];

  async function create() {
    if (!form.title.trim() || !form.slug.trim()) { actions.toast('Title and slug are required', 'error'); return; }
    if (mode === 'builder' && !selectedLayout) { actions.toast('Please select a layout', 'error'); return; }

    // Custom HTML validation
    if (mode === 'template' && htmlMode === 'custom') {
      if (!customHtml.trim()) { setHtmlError('Please paste your HTML template code.'); return; }
      const keyCount = countCmsKeys(customHtml);
      if (keyCount === 0) {
        setHtmlError('No data-cms-key attributes found. Add data-cms-key="section.field.key" to every editable element before saving.');
        return;
      }
      setHtmlError('');
    }

    setBusy(true);
    try {
      const slug = form.slug.trim();

      // If custom HTML mode → save the template file via API first
      if (mode === 'template' && htmlMode === 'custom') {
        await api.saveTemplate(state.auth.accessToken, slug, customHtml);
        actions.toast(`📄 Template "${slug}.html" saved to server`, 'info');
      }

      const templateSlug = mode === 'builder' ? selectedLayout.template : (htmlMode === 'custom' ? slug : form.template);
      const layoutJson = [{
        type: 'LegacyCmsPage',
        props: { page: slug, template: templateSlug, keys: {}, collections: {}, showInFooter: form.showInFooter },
      }];
      const payload = {
        title: form.title, slug,
        navLabel: form.navLabel || form.title, showInNav: form.showInNav,
        navOrder: Number(form.navOrder || 99), metaDescription: form.metaDescription || null, layoutJson,
      };
      const created = await api.createPage(state.auth.accessToken, payload);
      actions.setPages([...(state.pages.list || []), created]);
      actions.toast(`✨ Page "${form.title}" created!`, 'success');
      onCreated(created);
      onClose();
    } catch (e) { actions.toast(e.message || 'Failed to create page', 'error'); }
    finally { setBusy(false); }
  }

  // ── Step 1: Mode selection ──────────────────────────────
  if (step === 'mode') {
    return (
      <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
        <div className="bg-white rounded-4 p-4" style={{ width: '100%', maxWidth: 560 }}>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0d0d16' }}>✨ Add New Page</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>How would you like to create your page?</div>
            </div>
            <button className="btn-close" onClick={onClose}></button>
          </div>

          <div className="row g-3">
            {[
              {
                key: 'template', icon: 'fa-file-code',
                title: 'Raw HTML Template',
                desc: 'Pick an existing HTML template file. Your data-cms-key attributes define the editable content areas in the visual editor.',
              },
              {
                key: 'builder', icon: 'fa-layer-group',
                title: 'Layout Builder',
                desc: 'Choose from 4 pre-built page layouts. Select a style, fill in your content, and publish — no HTML needed.',
              },
            ].map(opt => (
              <div key={opt.key} className="col-6">
                <button
                  className="btn w-100 h-100 text-start p-4 rounded-3"
                  onClick={() => { setMode(opt.key); setStep('config'); }}
                  style={{ border: '2px solid #e5e7eb', background: '#f9fafb', transition: 'border-color .15s, background .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#FF9500'; e.currentTarget.style.background = 'rgba(255,149,0,0.04)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <div className="text-center mb-3">
                    <i className={`fa-solid ${opt.icon}`} style={{ fontSize: 38, color: '#FF9500' }}></i>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#0d0d16', marginBottom: 6 }}>{opt.title}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>{opt.desc}</div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2: Configuration form ──────────────────────────
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div className="bg-white rounded-4 p-4" style={{ width: '100%', maxWidth: mode === 'builder' ? 660 : 500, maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0d0d16' }}>
            {mode === 'template' ? '📄 HTML Template Page' : '🎨 Layout Builder'}
          </div>
          <button className="btn-close" onClick={onClose}></button>
        </div>
        <button className="btn btn-link p-0 mb-4" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none' }} onClick={() => { setStep('mode'); setSelectedLayout(null); }}>
          ← Back to mode selection
        </button>

        {/* Layout Builder: 4 visual layout cards */}
        {mode === 'builder' && (
          <div className="mb-4">
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Choose a Layout *</div>
            <div className="row g-2">
              {LAYOUTS.map(layout => (
                <div key={layout.id} className="col-6">
                  <button
                    className="btn w-100 text-start p-3 rounded-3"
                    onClick={() => setSelectedLayout(layout)}
                    style={{
                      border: `2px solid ${selectedLayout?.id === layout.id ? '#FF9500' : '#e5e7eb'}`,
                      background: selectedLayout?.id === layout.id ? 'rgba(255,149,0,0.06)' : '#f9fafb',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className={`fa-solid ${layout.icon}`} style={{ color: '#FF9500', fontSize: 13 }}></i>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#0d0d16' }}>{layout.label}</span>
                      {selectedLayout?.id === layout.id && <i className="fa-solid fa-circle-check ms-auto" style={{ color: '#FF9500', fontSize: 12 }}></i>}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>{layout.desc}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#9ca3af', background: '#f3f4f6', padding: '5px 7px', borderRadius: 5, lineHeight: 1.7, whiteSpace: 'pre' }}>{layout.preview}</div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HTML Template picker — with sub-mode toggle */}
        {mode === 'template' && (
          <div className="mb-3">
            {/* Sub-mode toggle */}
            <div className="d-flex gap-2 mb-3">
              {[
                { key: 'existing', label: 'Use Existing Template', icon: 'fa-folder-open' },
                { key: 'custom', label: 'Paste Custom HTML', icon: 'fa-code' },
              ].map(opt => (
                <button key={opt.key} className="btn btn-sm flex-fill"
                  onClick={() => { setHtmlMode(opt.key); setHtmlError(''); }}
                  style={{
                    border: `2px solid ${htmlMode === opt.key ? '#FF9500' : '#e5e7eb'}`,
                    background: htmlMode === opt.key ? 'rgba(255,149,0,0.08)' : '#f9fafb',
                    color: htmlMode === opt.key ? '#c27000' : '#374151',
                    fontWeight: htmlMode === opt.key ? 700 : 500,
                    fontSize: 12,
                  }}>
                  <i className={`fa-solid ${opt.icon} me-2`}></i>{opt.label}
                </button>
              ))}
            </div>

            {/* Existing template dropdown */}
            {htmlMode === 'existing' && (
              <>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>HTML Template *</label>
                <select className="form-select mb-1" value={form.template} onChange={e => set('template', e.target.value)}>
                  {TEMPLATES.map(t => <option key={t.value} value={t.value}>{t.value}.html — {t.desc}</option>)}
                </select>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                  The HTML file used to render this page. Add <code style={{ fontSize: 9 }}>data-cms-key</code> attributes to make sections editable.
                </div>
              </>
            )}

            {/* Custom HTML textarea */}
            {htmlMode === 'custom' && (
              <div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 0 }}>Paste HTML Template *</label>
                  {/* Live key count badge */}
                  {customHtml.trim().length > 0 && (
                    <span className={`badge ${liveKeyCount > 0 ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: 11 }}>
                      {liveKeyCount > 0
                        ? <><i className="fa-solid fa-check me-1"></i>{liveKeyCount} data-cms-key{liveKeyCount !== 1 ? 's' : ''} found</>
                        : <><i className="fa-solid fa-triangle-exclamation me-1"></i>0 data-cms-key found</>}
                    </span>
                  )}
                </div>

                <textarea
                  className="form-control font-monospace"
                  rows={10}
                  value={customHtml}
                  onChange={e => { setCustomHtml(e.target.value); setHtmlError(''); }}
                  placeholder={`Paste your full HTML template here.\n\nEvery editable element MUST have a data-cms-key attribute, e.g.:\n\n<h1 data-cms-key="about.hero.content.heading1.text">Page Title</h1>\n<p data-cms-key="about.hero.content.text1.text">Description here.</p>`}
                  style={{ fontSize: 11, lineHeight: 1.6, minHeight: 200, borderColor: htmlError ? '#ef4444' : liveKeyCount > 0 && customHtml ? '#22c55e' : undefined }}
                  spellCheck={false}
                />

                {/* Error message */}
                {htmlError && (
                  <div className="mt-2 p-2 rounded-2 d-flex align-items-start gap-2"
                    style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.3)', fontSize: 12, color: '#b91c1c' }}>
                    <i className="fa-solid fa-circle-xmark mt-1" style={{ flexShrink: 0 }}></i>
                    <span>{htmlError}</span>
                  </div>
                )}

                {/* No-error success hint */}
                {!htmlError && liveKeyCount > 0 && (
                  <div className="mt-2 p-2 rounded-2 d-flex align-items-start gap-2"
                    style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.3)', fontSize: 12, color: '#15803d' }}>
                    <i className="fa-solid fa-circle-check mt-1" style={{ flexShrink: 0 }}></i>
                    <span><strong>{liveKeyCount} data-cms-key</strong> attribute{liveKeyCount !== 1 ? 's' : ''} found. This HTML will be saved as <code style={{ fontSize: 11 }}>{form.slug || 'page-slug'}.html</code> and linked to the page.</span>
                  </div>
                )}

                {/* Key format hint + expandable example */}
                {!customHtml.trim() && (
                  <div className="mt-2 rounded-2" style={{ border: '1px solid #e5e7eb', overflow: 'hidden', fontSize: 11 }}>
                    {/* Info bar */}
                    <div className="p-2 d-flex align-items-start gap-2" style={{ background: '#f0f9ff', borderBottom: '1px solid #bae6fd' }}>
                      <i className="fa-solid fa-circle-info mt-1" style={{ color: '#0284c7', flexShrink: 0 }}></i>
                      <div style={{ color: '#0369a1', lineHeight: 1.6 }}>
                        <strong>Required format:</strong> Every editable element must have a <code style={{ fontSize: 10, background: 'rgba(2,132,199,0.1)', padding: '0 3px', borderRadius: 3 }}>data-cms-key</code> attribute.<br />
                        Pattern: <code style={{ fontSize: 10, background: 'rgba(2,132,199,0.1)', padding: '0 3px', borderRadius: 3 }}>page-slug.section-id.content.field-name.text</code>
                      </div>
                    </div>
                    {/* Example toggle button */}
                    <div className="px-2 py-2" style={{ background: '#f9fafb' }}>
                      <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center gap-2"
                        style={{ fontSize: 11, padding: '3px 10px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 6 }}
                        onClick={() => setShowExample(v => !v)}
                      >
                        <i className={`fa-solid fa-${showExample ? 'chevron-up' : 'code'}`}></i>
                        {showExample ? 'Hide example template' : 'View example template'}
                      </button>
                    </div>
                    {/* Expandable code block */}
                    {showExample && (
                      <div style={{ borderTop: '1px solid #334155' }}>
                        {/* Code header */}
                        <div className="px-3 py-2 d-flex align-items-center justify-content-between" style={{ background: '#1e293b' }}>
                          <div className="d-flex align-items-center gap-2">
                            <i className="fa-solid fa-file-code" style={{ color: '#94a3b8', fontSize: 11 }}></i>
                            <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>example-template.html</span>
                            <span className="badge" style={{ fontSize: 9, background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>
                              8 data-cms-keys
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ fontSize: 10, padding: '2px 10px', background: '#FF9500', border: 'none', color: '#fff', fontWeight: 700, borderRadius: 5 }}
                            onClick={() => { setCustomHtml(EXAMPLE_TEMPLATE); setHtmlError(''); setShowExample(false); }}
                          >
                            <i className="fa-solid fa-paste me-1"></i>Use this example
                          </button>
                        </div>
                        {/* Code block */}
                        <pre style={{ background: '#0f172a', color: '#e2e8f0', fontSize: 10, lineHeight: 1.6, margin: 0, padding: '12px 14px', maxHeight: 320, overflowY: 'auto', whiteSpace: 'pre', overflowX: 'auto' }}>
{`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Page Title</title>
  <link rel="stylesheet" href="/assets/css/styles.css" />
</head>
<body>

  <!-- Replace "your-page" with your actual page slug -->

  <!-- HERO SECTION -->
  <section class="hero-section">
    <div class="container">
      <h1 `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.hero.content.heading1.text</span>{`">Page Heading</h1>
      <p `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.hero.content.text1.text</span>{`">Subtitle goes here.</p>
      <a href="#" `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.hero.content.button1.text</span>{`">Get Started</a>
    </div>
  </section>

  <!-- MAIN CONTENT SECTION -->
  <section class="content-section">
    <div class="container">
      <h2 `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.main.content.heading1.text</span>{`">Main Section Title</h2>
      <p `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.main.content.text1.text</span>{`">First paragraph content.</p>
      <p `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.main.content.text2.text</span>{`">Second paragraph content.</p>
    </div>
  </section>

  <!-- CALL TO ACTION SECTION -->
  <section class="cta-section">
    <div class="container">
      <h2 `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.cta.content.heading1.text</span>{`">Ready to Get Started?</h2>
      <p `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.cta.content.text1.text</span>{`">Contact us today.</p>
      <a href="/contact" `}<span style={{ color: '#fbbf24' }}>data-cms-key</span>{`="`}<span style={{ color: '#86efac' }}>your-page.cta.content.button1.text</span>{`">Contact Us</a>
    </div>
  </section>

</body>
</html>`}
                        </pre>
                        {/* Key format reference table */}
                        <div className="p-2" style={{ background: '#1e293b', borderTop: '1px solid #334155' }}>
                          <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Key format breakdown</div>
                          <div className="d-flex align-items-stretch gap-0" style={{ fontFamily: 'monospace', fontSize: 10, border: '1px solid #334155', borderRadius: 4, overflow: 'hidden' }}>
                            {[
                              { part: 'your-page', label: 'page slug', color: '#86efac' },
                              { part: 'section-id', label: 'section name', color: '#7dd3fc' },
                              { part: 'content', label: 'fixed word', color: '#d1d5db' },
                              { part: 'field-name', label: 'field name', color: '#fbbf24' },
                              { part: 'text', label: 'type', color: '#f9a8d4' },
                            ].map((item, i, arr) => (
                              <div key={i} className="text-center" style={{ flex: 1, padding: '4px 2px', background: '#0f172a', borderRight: i < arr.length - 1 ? '1px solid #334155' : 'none' }}>
                                <div style={{ color: item.color, fontWeight: 700 }}>{item.part}</div>
                                <div style={{ color: '#64748b', fontSize: 8 }}>{item.label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Page Title */}
        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Page Title *</label>
        <input className="form-control mb-3" value={form.title} placeholder="e.g. About Us"
          onChange={e => { set('title', e.target.value); set('slug', autoSlug(e.target.value)); set('navLabel', e.target.value); }} />

        {/* Slug */}
        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>URL Slug *</label>
        <input className="form-control mb-1" value={form.slug} placeholder="e.g. about-us"
          onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 14 }}>
          Public URL: <span style={{ fontFamily: 'monospace', color: '#FF9500' }}>/{form.slug || 'page-slug'}</span>
        </div>

        {/* Navigation Placement */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            <i className="fa-solid fa-compass me-2" style={{ color: '#FF9500' }}></i>Navigation Placement
          </div>
          <div className="d-flex gap-4 flex-wrap mb-2">
            <div className="form-check">
              <input type="checkbox" className="form-check-input" id="npShowInNav" checked={form.showInNav} onChange={e => set('showInNav', e.target.checked)} />
              <label className="form-check-label" htmlFor="npShowInNav" style={{ fontSize: 13 }}>
                <i className="fa-solid fa-bars me-1" style={{ color: '#6b7280' }}></i> Header Navigation
              </label>
            </div>
            <div className="form-check">
              <input type="checkbox" className="form-check-input" id="npShowInFooter" checked={form.showInFooter} onChange={e => set('showInFooter', e.target.checked)} />
              <label className="form-check-label" htmlFor="npShowInFooter" style={{ fontSize: 13 }}>
                <i className="fa-solid fa-grip-lines me-1" style={{ color: '#6b7280' }}></i> Footer Links
              </label>
            </div>
          </div>
          {form.showInNav && (
            <div className="mt-2">
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Nav Label</label>
              <input className="form-control form-control-sm" style={{ maxWidth: 220 }}
                value={form.navLabel} placeholder="Label shown in menu"
                onChange={e => set('navLabel', e.target.value)} />
            </div>
          )}
          {form.showInFooter && (
            <div className="mt-2 p-2 rounded-2" style={{ background: 'rgba(255,149,0,0.06)', fontSize: 11, color: '#92400e', border: '1px solid rgba(255,149,0,0.2)' }}>
              <i className="fa-solid fa-circle-info me-1"></i>
              Footer links in HTML templates are static. After creating this page, add the link manually to your footer template HTML.
            </div>
          )}
        </div>

        {/* Meta Description */}
        <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Meta Description</label>
        <textarea className="form-control mb-4" rows={2} value={form.metaDescription} placeholder="SEO description for search engines…"
          onChange={e => set('metaDescription', e.target.value)} />

        <div className="d-flex gap-2 justify-content-end">
          <button className="btn btn-outline-secondary" onClick={() => { setStep('mode'); setSelectedLayout(null); }}>← Back</button>
          <button className="btn btn-primary" onClick={create} disabled={busy || (mode === 'builder' && !selectedLayout)}
            style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}>
            {busy
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
              : <><i className="fa-solid fa-plus me-2"></i>Create Page</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==============================================
// PAGES LIST VIEW
// ==============================================
function PagesListView({ onEditPage, onAddPage }) {
  const { state, actions } = useStore();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await api.listPages(state.auth.accessToken);
        if (!cancelled) actions.setPages(list);
      } catch (e) {
        if (!cancelled) actions.toast(e.message || 'Failed to load pages', 'error');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [state.auth.accessToken]);

  const pages = (state.pages.list || []).filter(p =>
    !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.slug?.toLowerCase().includes(search.toLowerCase())
  );

  async function handlePublish(p) {
    if (window.confirm(`Publish "${p.title}" immediately?`)) {
      setLoading(true);
      try {
        await api.publishPage(state.auth.accessToken, p.id);
        const updated = await api.getPage(state.auth.accessToken, p.id);
        actions.cachePage(p.id, updated);
        actions.setPages(state.pages.list.map(x => x.id === p.id ? updated : x));
        actions.toast('🚀 Page published!', 'success');
      } catch (e) {
        actions.toast(e.message || 'Publish failed', 'error');
      } finally {
        setLoading(false);
      }
    }
  }

  async function handleUnpublish(p) {
    if (window.confirm(`Unpublish "${p.title}" and revert to draft?`)) {
      setLoading(true);
      try {
        await api.unpublishPage(state.auth.accessToken, p.id);
        const updated = await api.getPage(state.auth.accessToken, p.id);
        actions.cachePage(p.id, updated);
        actions.setPages(state.pages.list.map(x => x.id === p.id ? updated : x));
        actions.toast('🛑 Page unpublished.', 'success');
      } catch (e) {
        actions.toast(e.message || 'Unpublish failed', 'error');
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'black' }}>All Pages</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>Manage your site pages and content</div>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <input className="form-control" style={{ width: 220, padding: '8px 14px', fontSize: 13, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)', color: '#e8e8f4' }} placeholder="Search pages…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={onAddPage} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}><i className="fa-solid fa-plus me-1"></i> New Page</button>
        </div>
      </div>

      <div className="bg-secondary bg-opacity-10 rounded-3 overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="row p-3 m-0" style={{ background: 'rgba(0, 0, 0, 0.32)', borderBottom: '1px solid rgba(14, 13, 13, 0.07)' }}>
          <div className="col-3" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Title</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Slug</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Status</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Placement</div>
          <div className="col-1" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Updated</div>
          <div className="col-2" style={{ fontSize: 10, fontWeight: 700, color: '#0000009d', textTransform: 'uppercase', letterSpacing: '0.1em' }}></div>
        </div>

        {loading && (
          <div className="text-center p-5">
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <div style={{ fontSize: 13, color: 'black' }}>Loading pages…</div>
          </div>
        )}

        {!loading && pages.map((p, i) => (
          <div key={p.id} className="row p-3 m-0 align-items-center" style={{ borderBottom: i < pages.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
            <div className="col-3">
              <div style={{ fontSize: 13, fontWeight: 600, color: 'black' }}>{p.title}</div>
              {p.metaDescription && <div style={{ fontSize: 11, color: '#00000000', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }}>{p.metaDescription}</div>}
            </div>
            <div className="col-2" style={{ fontSize: 11, color: 'black', fontFamily: 'monospace' }}>/{p.slug}</div>
            <div className="col-2"><span className={`badge ${p.status === 'PUBLISHED' ? 'bg-success' : 'bg-warning'}`}>{p.status === 'PUBLISHED' ? 'Live' : 'Draft'}</span></div>
            <div className="col-2 d-flex gap-1 flex-wrap">
              {p.showInNav
                ? <span className="badge" style={{ fontSize: 9, background: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)' }}><i className="fa-solid fa-compass me-1"></i>Header</span>
                : <span className="badge" style={{ fontSize: 9, background: 'rgba(107,114,128,0.1)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.2)' }}>No header</span>}
              {p.showInFooter
                ? <span className="badge" style={{ fontSize: 9, background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}><i className="fa-solid fa-layer-group me-1"></i>Footer</span>
                : null}
            </div>
            <div className="col-1" style={{ fontSize: 11, color: '#6b7280' }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : '–'}</div>
            <div className="col-2 text-end" style={{ whiteSpace: 'nowrap' }}>
              {p.status !== 'PUBLISHED' ? (
                <button className="btn btn-sm btn-outline-success me-2" onClick={() => handlePublish(p)} title="Publish"><i className="fa-solid fa-rocket"></i> Publish</button>
              ) : (
                <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleUnpublish(p)} title="Unpublish"><i className="fa-solid fa-eye-slash"></i> Unpublish</button>
              )}
              <button className="btn btn-sm btn-primary" onClick={() => onEditPage(p)} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }} title="Edit"><i className="fa-solid fa-pen-to-square"></i></button>
            </div>
          </div>
        ))}

        {!loading && pages.length === 0 && (
          <div className="text-center p-5">
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'white', marginBottom: 6 }}>No pages found</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{search ? 'Try a different search term.' : 'Get started by creating your first page.'}</div>
            {!search && <button className="btn btn-primary" onClick={onAddPage} style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none' }}><i className="fa-solid fa-plus me-1"></i> Create First Page</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ==============================================
// NAVIGATION MANAGER — helper row
// ==============================================
function NavPageRow({ page, onRemove, showOrder, onOrderChange, busyId }) {
  const [order, setOrder] = useState(page.navOrder || 0);
  const isDirty = order !== (page.navOrder || 0);
  const isBusy = busyId === page.id;
  return (
    <div className="d-flex align-items-center gap-2 p-2 rounded-2 mb-2"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
      {showOrder && (
        <span style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(255,149,0,0.12)', color: '#FF9500', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {page.navOrder || 0}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.title}</div>
        <div style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace' }}>/{page.slug}</div>
      </div>
      <span className={`badge ${page.status === 'PUBLISHED' ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: 9 }}>
        {page.status === 'PUBLISHED' ? 'Live' : 'Draft'}
      </span>
      {showOrder && (
        <div className="d-flex align-items-center gap-1">
          <input
            type="number" min={1}
            className="form-control form-control-sm"
            value={order}
            onChange={e => setOrder(Number(e.target.value))}
            style={{ width: 54, fontSize: 11, padding: '2px 6px', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: 'white', textAlign: 'center' }}
          />
          {isDirty && (
            <button className="btn btn-sm" title="Save order" onClick={() => onOrderChange(page, order)}
              style={{ background: '#22c55e', color: 'white', border: 'none', padding: '2px 7px', fontSize: 11, borderRadius: 5 }}>
              <i className="fa-solid fa-check"></i>
            </button>
          )}
        </div>
      )}
      <button className="btn btn-sm" title="Remove" disabled={isBusy} onClick={() => onRemove(page)}
        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', padding: '3px 9px', fontSize: 12, borderRadius: 6 }}>
        {isBusy ? <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} /> : <i className="fa-solid fa-xmark"></i>}
      </button>
    </div>
  );
}

function NavSection({ icon, iconColor, title, subtitle, items, onRemove, showOrder, onOrderChange, addPlaceholder, addOptions, addValue, onAddChange, onAdd, busyId, addBusy }) {
  return (
    <div className="rounded-3 p-3 mb-4" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.08)' }}>
      {/* Section header */}
      <div className="d-flex align-items-center gap-3 mb-3 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: `${iconColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`fa-solid ${icon}`} style={{ color: iconColor, fontSize: 16 }}></i>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>{title}</div>
          <div style={{ fontSize: 11, color: '#6b7280' }}>{subtitle}</div>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: `${iconColor}18`, color: iconColor }}>
          {items.length} page{items.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Page rows */}
      {items.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-4" style={{ border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 10, color: '#4b5563' }}>
          <i className={`fa-solid ${icon} mb-2`} style={{ fontSize: 22, opacity: 0.25 }}></i>
          <div style={{ fontSize: 12 }}>No pages added yet</div>
        </div>
      ) : (
        <div className="mb-3">
          {items.map(p => (
            <NavPageRow key={p.id} page={p} onRemove={onRemove} showOrder={showOrder} onOrderChange={onOrderChange} busyId={busyId} />
          ))}
        </div>
      )}

      {/* Add page row */}
      <div className="d-flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <select className="form-select form-select-sm" value={addValue} onChange={e => onAddChange(e.target.value)}
          style={{ fontSize: 12, background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', color: addValue ? 'white' : '#6b7280' }}>
          <option value="">{addPlaceholder}</option>
          {addOptions.map(p => <option key={p.id} value={p.id}>{p.title}  /{p.slug}</option>)}
        </select>
        <button className="btn btn-sm d-flex align-items-center gap-1" disabled={!addValue || addBusy} onClick={onAdd}
          style={{ background: 'linear-gradient(135deg,#FF9500,#FFB300)', border: 'none', color: 'white', fontWeight: 700, whiteSpace: 'nowrap', fontSize: 12, padding: '0 14px', borderRadius: 7 }}>
          {addBusy ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <i className="fa-solid fa-plus"></i>}
          <span>Add</span>
        </button>
      </div>
    </div>
  );
}

// ==============================================
// NAVIGATION MANAGER VIEW
// ==============================================
function NavigationManagerView() {
  const { state, actions } = useStore();
  const [busyId, setBusyId] = useState('');
  const [addNavId, setAddNavId] = useState('');
  const [addFooterId, setAddFooterId] = useState('');
  const [addNavBusy, setAddNavBusy] = useState(false);
  const [addFooterBusy, setAddFooterBusy] = useState(false);

  // Load pages if not already loaded
  useEffect(() => {
    if (state.pages.list.length > 0) return;
    (async () => {
      try {
        const list = await api.listPages(state.auth.accessToken);
        actions.setPages(list);
      } catch (e) {
        actions.toast(e.message || 'Failed to load pages', 'error');
      }
    })();
  }, [state.auth.accessToken]);

  const allPages = state.pages.list || [];
  const navPages = [...allPages.filter(p => p.showInNav)].sort((a, b) => (a.navOrder || 0) - (b.navOrder || 0));
  const footerPages = [...allPages.filter(p => p.showInFooter)].sort((a, b) => (a.navOrder || 0) - (b.navOrder || 0));
  const notInNav = allPages.filter(p => !p.showInNav);
  const notInFooter = allPages.filter(p => !p.showInFooter);

  async function applyPatch(page, payload, msg) {
    setBusyId(page.id);
    try {
      await api.patchPage(state.auth.accessToken, page.id, payload);
      actions.setPages(allPages.map(p => p.id === page.id ? { ...p, ...payload } : p));
      actions.toast(msg, 'success');
    } catch (e) {
      actions.toast(e.message || 'Update failed', 'error');
    } finally {
      setBusyId('');
    }
  }

  async function addToNav() {
    const page = allPages.find(p => p.id === addNavId);
    if (!page) return;
    setAddNavBusy(true);
    try {
      const newOrder = navPages.length + 1;
      await api.patchPage(state.auth.accessToken, page.id, { showInNav: true, navOrder: newOrder });
      actions.setPages(allPages.map(p => p.id === page.id ? { ...p, showInNav: true, navOrder: newOrder } : p));
      actions.toast(`"${page.title}" added to header nav`, 'success');
      setAddNavId('');
    } catch (e) {
      actions.toast(e.message || 'Failed to add', 'error');
    } finally {
      setAddNavBusy(false);
    }
  }

  async function addToFooter() {
    const page = allPages.find(p => p.id === addFooterId);
    if (!page) return;
    setAddFooterBusy(true);
    try {
      await api.patchPage(state.auth.accessToken, page.id, { showInFooter: true });
      actions.setPages(allPages.map(p => p.id === page.id ? { ...p, showInFooter: true } : p));
      actions.toast(`"${page.title}" added to footer links`, 'success');
      setAddFooterId('');
    } catch (e) {
      actions.toast(e.message || 'Failed to add', 'error');
    } finally {
      setAddFooterBusy(false);
    }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
      {/* Page heading */}
      <div className="mb-4">
        <div style={{ fontSize: 22, fontWeight: 800, color: 'black' }}>Navigation Manager</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>Control which pages appear in the public site header and footer</div>
      </div>

      {/* Info banner */}
      <div className="d-flex align-items-start gap-2 p-3 rounded-3 mb-4"
        style={{ background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.2)', fontSize: 12, color: '#78350f' }}>
        <i className="fa-solid fa-bolt mt-1" style={{ color: '#FF9500', flexShrink: 0 }}></i>
        <span>
          Changes are <strong>live immediately</strong> — removing a page hides it from the public site on the next visitor page load.
          Only <strong>Published</strong> pages appear to visitors regardless of placement.
        </span>
      </div>

      <div style={{ maxWidth: 720 }}>
        {/* Header Navigation */}
        <NavSection
          icon="fa-compass"
          iconColor="#3b82f6"
          title="Header Navigation"
          subtitle="Displayed in the top navigation bar on every page"
          items={navPages}
          onRemove={p => applyPatch(p, { showInNav: false }, `"${p.title}" removed from header nav`)}
          showOrder={true}
          onOrderChange={(p, order) => applyPatch(p, { navOrder: order }, `Nav order updated for "${p.title}"`)}
          addPlaceholder="— Select a page to add to header nav —"
          addOptions={notInNav}
          addValue={addNavId}
          onAddChange={setAddNavId}
          onAdd={addToNav}
          busyId={busyId}
          addBusy={addNavBusy}
        />

        {/* Footer Links */}
        <NavSection
          icon="fa-layer-group"
          iconColor="#22c55e"
          title="Footer Links"
          subtitle="Displayed in the Quick Links section in the page footer"
          items={footerPages}
          onRemove={p => applyPatch(p, { showInFooter: false }, `"${p.title}" removed from footer links`)}
          showOrder={false}
          onOrderChange={null}
          addPlaceholder="— Select a page to add to footer links —"
          addOptions={notInFooter}
          addValue={addFooterId}
          onAddChange={setAddFooterId}
          onAdd={addToFooter}
          busyId={busyId}
          addBusy={addFooterBusy}
        />
      </div>
    </div>
  );
}

// ==============================================
// STATUS BAR
// ==============================================
function StatusBar({ loading, pageTitle, editMode }) {
  return (
    <div style={{ height: 28, minHeight: 28, background: '#13131f', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 16, padding: '0 14px', fontSize: 11, color: '#6b7280', flexShrink: 0 }}>
      <span className="d-flex align-items-center gap-1">
        <span style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', background: loading ? '#f59e0b' : '#22c55e' }} />
        {loading ? 'Working…' : 'Ready'}
      </span>
      {pageTitle && <span style={{ color: '#9ca3af' }}>Editing: <span style={{ color: 'white', fontWeight: 600 }}>{pageTitle}</span></span>}
      {editMode !== undefined && <span>Mode: <span style={{ color: '#FF9500', fontWeight: 600 }}>{editMode ? 'Edit' : 'Preview'}</span></span>}
      <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'rgba(255,149,0,0.4)', fontSize: 10, letterSpacing: '0.1em' }}>RAILCAB CMS v1.0</span>
    </div>
  );
}

// ==============================================
// MAIN APP
// ==============================================
function App() {
  const { state, actions } = useStore();
  const { route, navigate } = useRouter();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('pages');
  const [selectedPage, setSelectedPage] = useState(null);
  const [showAddPage, setShowAddPage] = useState(false);
  const [editMode, setEditMode] = useState(true);

  const isLoggedIn = !!state.auth.accessToken;

  useEffect(() => {
    if (!state.auth.accessToken || state.auth.user) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await api.me(state.auth.accessToken);
        if (!cancelled) actions.setUser(me);
      } catch { if (!cancelled) actions.logout(); }
    })();
    return () => { cancelled = true; };
  }, [state.auth.accessToken]);

  if (!isLoggedIn) return <LoginPage />;

  function handleEditPage(page) {
    setSelectedPage(page);
    setCurrentView('editor');
  }

  function handleNavigate(view) {
    setCurrentView(view);
  }

  const pages = state.pages.list || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} currentView={currentView} onNavigate={handleNavigate} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <TopBar
            currentPage={selectedPage}
            pages={pages}
            onPageChange={p => { setSelectedPage(p); setCurrentView('editor'); }}
            editMode={editMode}
            onEditToggle={() => setEditMode(v => !v)}
            onAddPage={() => setShowAddPage(true)}
            loading={state.ui.loading}
            isSidebarCollapsed={sidebarCollapsed}
            onSidebarToggle={() => setSidebarCollapsed(v => !v)}
          />

          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {currentView === 'pages' && (
              <PagesListView onEditPage={handleEditPage} onAddPage={() => setShowAddPage(true)} />
            )}
            {currentView === 'navigation' && (
              <NavigationManagerView />
            )}
            {currentView === 'editor' && (
              <VisualEditorPage
                selectedPageId={selectedPage?.id}
                onPageChange={setSelectedPage}
                pages={pages}
              />
            )}
          </div>

          <StatusBar loading={state.ui.loading} pageTitle={selectedPage?.title} editMode={currentView === 'editor' ? editMode : undefined} />
        </div>
      </div>

      {showAddPage && (
        <AddPageModal
          onClose={() => setShowAddPage(false)}
          onCreated={page => { setSelectedPage(page); setCurrentView('editor'); }}
        />
      )}

      <ToastContainer />
    </div>
  );
}

// ==============================================
// EXPORT
// ==============================================
export default function RailCabCMS() {
  return (
    <StoreProvider>
      <RouterProvider>
        <App />
      </RouterProvider>
    </StoreProvider>
  );
}